// Sign in with Apple / Google buttons for the login + register pages. Each
// provider is gated on its web client id (VITE_GOOGLE_WEB_CLIENT_ID /
// VITE_APPLE_WEB_CLIENT_ID): with neither set this renders nothing, so the UI
// never shows a broken button before the one-time Apple/Google console setup is
// done. On success we exchange the provider id_token at the backend, refresh the
// session, and navigate to `next`.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth-context";
import { oauthApple, oauthGoogle } from "@/api/auth";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined;
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_WEB_CLIENT_ID as string | undefined;

const APPLE_JS =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const GOOGLE_JS = "https://accounts.google.com/gsi/client";

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.id = id;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

export function SocialAuthButtons({
  next,
  onError,
}: {
  next: string;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const googleRef = useRef<HTMLDivElement>(null);

  // Render Google's official button once the GIS script is loaded.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleRef.current) return;
    let cancelled = false;
    void loadScript(GOOGLE_JS, "google-gsi")
      .then(() => {
        if (cancelled) return;
        const g = (window as unknown as { google?: any }).google;
        if (!g || !googleRef.current) return;
        g.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (resp: { credential: string }) => {
            try {
              await oauthGoogle(resp.credential);
              await refreshMe();
              navigate(next, { replace: true });
            } catch (err) {
              onError(err instanceof Error ? err.message : t("Couldn't sign in with Google."));
            }
          },
        });
        g.accounts.id.renderButton(googleRef.current, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          width: 320,
        });
      })
      .catch(() => onError(t("Couldn't load Google sign-in.")));
    return () => {
      cancelled = true;
    };
  }, [next, refreshMe, navigate, onError, t]);

  const onApple = async () => {
    if (!APPLE_CLIENT_ID) return;
    try {
      await loadScript(APPLE_JS, "apple-js");
      const AppleID = (window as unknown as { AppleID?: any }).AppleID;
      AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: window.location.origin + "/login",
        usePopup: true,
      });
      const resp = await AppleID.auth.signIn();
      const idToken: string = resp.authorization.id_token;
      // Apple includes the name only on the first authorization.
      const name = resp.user?.name;
      const fullName = name
        ? `${name.firstName ?? ""} ${name.lastName ?? ""}`.trim() || undefined
        : undefined;
      await oauthApple(idToken, fullName);
      await refreshMe();
      navigate(next, { replace: true });
    } catch (err) {
      // Apple throws { error: "popup_closed_by_user" } on cancel — ignore that.
      if (err && typeof err === "object" && "error" in err) return;
      onError(err instanceof Error ? err.message : t("Couldn't sign in with Apple."));
    }
  };

  if (!GOOGLE_CLIENT_ID && !APPLE_CLIENT_ID) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs text-ink-faint">
        <span className="h-px flex-1 bg-black/10" />
        {t("or")}
        <span className="h-px flex-1 bg-black/10" />
      </div>
      {GOOGLE_CLIENT_ID && <div ref={googleRef} className="flex justify-center" />}
      {APPLE_CLIENT_ID && (
        <button
          type="button"
          onClick={onApple}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-black px-4 py-3 text-sm font-semibold text-white transition hover:bg-black/90"
        >
          <svg viewBox="0 0 24 24" aria-hidden className="h-5 w-5 fill-current">
            <path d="M16.36 12.78c-.02-2.16 1.76-3.2 1.84-3.25-1-1.47-2.56-1.67-3.12-1.69-1.33-.13-2.6.78-3.27.78-.67 0-1.71-.76-2.81-.74-1.45.02-2.78.84-3.53 2.14-1.5 2.61-.38 6.48 1.08 8.6.71 1.04 1.56 2.2 2.67 2.16 1.07-.04 1.48-.69 2.77-.69 1.29 0 1.66.69 2.79.67 1.15-.02 1.88-1.05 2.59-2.09.81-1.2 1.15-2.36 1.16-2.42-.03-.01-2.23-.86-2.25-3.4zM14.3 6.24c.59-.72.99-1.71.88-2.7-.85.03-1.88.57-2.49 1.28-.55.63-1.03 1.64-.9 2.61.95.07 1.92-.48 2.51-1.19z" />
          </svg>
          {t("Continue with Apple")}
        </button>
      )}
    </div>
  );
}
